'use client';

import { Info } from 'lucide-react';

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import type { ShabbatTimesInfo } from '@/lib/shabbat';
import { cn } from '@/lib/utils';

/**
 * Weekend Shabbat times for Ashkelon - a single horizontal row of facts
 * under the homepage featured story.
 */
export function HomeShabbatStrip({
    times,
    className,
}: {
    times: ShabbatTimesInfo | null | undefined;
    className?: string;
}) {
    if (!times) return null;

    return (
        <section
            className={cn('border border-white/10 px-3 py-2.5 sm:px-4 sm:py-3', className)}
            aria-label="זמני שבת באשקלון"
        >
            <p className="flex min-w-0 flex-nowrap items-baseline gap-x-2 overflow-hidden text-xs sm:gap-x-3 sm:text-sm">
                <span className="inline-flex shrink-0 items-center gap-1">
                    <span className="text-white/55">שבת באשקלון</span>
                    <ShabbatMethodNote />
                </span>
                <Fact
                    label="כניסה"
                    dateTime={`${times.fridayKey}T${times.entranceLabel}:00`}
                    value={times.entranceLabel}
                    time
                />
                <Fact
                    label="יציאה"
                    dateTime={`${times.saturdayKey}T${times.exitLabel}:00`}
                    value={times.exitLabel}
                    time
                />
                {times.parashaLabel ? (
                    <Fact value={times.parashaLabel} truncate />
                ) : null}
            </p>
        </section>
    );
}

function ShabbatMethodNote() {
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="inline-flex size-4 shrink-0 items-center justify-center text-white/45 transition-colors hover:text-white focus-visible:text-white focus-visible:outline-none"
                    aria-label="הסבר על חישוב זמני השבת באשקלון"
                >
                    <Info className="size-3.5" aria-hidden />
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                side="bottom"
                sideOffset={8}
                className="w-72 rounded-none border-white/10 bg-black p-4 text-start text-sm leading-relaxed text-white/70"
            >
                <p className="text-white">חישוב הזמנים</p>
                <p className="mt-2">
                    השעות מחושבות לפי מיקום אשקלון בלבד. אין להעתיק אותן לירושלים או ליישוב אחר.
                </p>
                <p className="mt-2">
                    כניסת שבת חלה 20 דקות לפני השקיעה ביום שישי, כמנהג המקובל באשקלון, ולא 40 דקות כמו בירושלים.
                </p>
                <p className="mt-2">
                    צאת שבת נקבעת לפי צאת הכוכבים, כשהשמש 8.5 מעלות מתחת לאופק. החישוב האסטרונומי זהה למנגנון המשמש בלוחות שנה מקובלים בישראל.
                </p>
            </PopoverContent>
        </Popover>
    );
}

function Fact({
    label,
    dateTime,
    value,
    time,
    truncate,
}: {
    label?: string;
    dateTime?: string;
    value: string;
    time?: boolean;
    truncate?: boolean;
}) {
    return (
        <span
            className={cn(
                'inline-flex min-w-0 items-baseline gap-1 sm:gap-1.5',
                truncate && 'overflow-hidden',
            )}
        >
            <span aria-hidden className="shrink-0 text-white/20">
                ·
            </span>
            {label ? <span className="shrink-0 text-white/55">{label}</span> : null}
            {time && dateTime ? (
                <time
                    dateTime={dateTime}
                    className="shrink-0 tabular-nums text-white"
                >
                    {value}
                </time>
            ) : (
                <span
                    className={cn(
                        'text-white/55',
                        truncate && 'min-w-0 truncate',
                    )}
                >
                    {value}
                </span>
            )}
        </span>
    );
}
